import { EstimateListingType } from "common/api/estimate.service";
import { ICalculatedFinancingData } from "common/records/CalculatedFinancing";
import { IDocumentData } from "common/records/Document";
import { IEstimateData } from "common/records/Estimate";
import { IEstimateCommissionData } from "common/records/EstimateCommission";
import { EstimateDiscountRecord, IEstimateDiscountData } from "common/records/EstimateDiscount";
import { IEstimateOverviewData } from "common/records/EstimateOverview";
import { PaymentTermRecord } from "common/records/PaymentTerm";
import { ActionsUnion, createAction } from "common/reducers/UtilsTypes";
import { IdType, Nullable } from "common/types";

export const RECEIVE_ESTIMATE_OVERVIEWS = "@estimates/RECEIVE_ESTIMATE_OVERVIEWS";
export const SET_CURRENT_ESTIMATE_ID = "@estimates/SET_CURRENT_ESTIMATE_ID";
export const FETCH_ESTIMATE = "@estimates/FETCH_ESTIMATE";
export const RECEIVE_ESTIMATE = "@estimates/RECEIVE_ESTIMATE";
export const SET_ESTIMATE_LOADED = "@estimates/SET_ESTIMATE_LOADED";
export const DISCARD_ESTIMATE = "@estimates/DISCARD_ESTIMATE";
export const SET_ESTIMATE_DOCS_LOADED = "@estimates/SET_ESTIMATE_DOCS_LOADED";
export const SET_CURRENT_LISTING_TYPE = "@estimates/SET_CURRENT_LISTING_TYPE";
export const RECEIVE_ESTIMATE_DOCUMENTS = "@estimates/RECEIVE_ESTIMATE_DOCUMENTS";
export const RESET_ESTIMATE_DOCUMENTS = "@estimates/RESET_ESTIMATE_DOCUMENTS";
export const UPDATE_ESTIMATE_TOTALS_TREE = "@estimates/UPDATE_ESTIMATE_TOTALS_TREE";
export const UPDATE_ESTIMATE_SUBTOTALS = "@estimates/UPDATE_ESTIMATE_SUBTOTALS";
export const UPDATE_ESTIMATE_DISCOUNT_TOTALS = "@estimates/UPDATE_ESTIMATE_DISCOUNT_TOTALS";
export const UPDATE_ESTIMATE_TAX_TOTALS = "@estimates/UPDATE_ESTIMATE_TAX_TOTALS";
export const UPDATE_ESTIMATE_PAYMENT_TERMS_TOTALS = "@estimates/UPDATE_ESTIMATE_PAYMENT_TERMS_TOTALS";
export const UPDATE_ESTIMATE_FINANCE_OPTION_PAYMENTS = "@estimates/UPDATE_ESTIMATE_FINANCE_OPTION_PAYMENTS";
export const UPDATE_ESTIMATE_TOTALS = "@estimates/UPDATE_ESTIMATE_TOTALS";
export const DECREMENT_ESTIMATE_UNSAVED_ID = "@estimates/DECREMENT_ESTIMATE_UNSAVED_ID";
export const SET_ESTIMATE_BY_ID = "@estimates/SET_ESTIMATE_BY_ID";
export const COPY_ESTIMATE_TO_LAST_SAVED = "@estimates/COPY_ESTIMATE_TO_LAST_SAVED";
export const BASELINE_PAYMENT_TERM_FINANCIALS = "@estimates/BASELINE_PAYMENT_TERM_FINANCIALS";
export const UPDATE_ESTIMATE_METADATA = "@estimates/UPDATE_ESTIMATE_METADATA";
export const SET_AUTO_OPEN_LINE_ITEM = "@estimates/SET_AUTO_OPEN_LINE_ITEM";
export const CONSUME_AUTO_OPEN_LINE_ITEM = "@estimates/CONSUME_AUTO_OPEN_LINE_ITEM";
export const FETCH_ESTIMATE_COMMISSION = "@estimates/FETCH_ESTIMATE_COMMISSION";
export const RECEIVE_ESTIMATE_COMMISSION = "@estimates/RECEIVE_ESTIMATE_COMMISSION";
export const ADD_DISCOUNT = "@estimates/ADD_DISCOUNT";
export const ADD_PAYMENT_TERM = "@estimates/ADD_PAYMENT_TERM";
export const RECEIVE_CALCULATIONS = "@estimates/RECEIVE_CALCULATIONS";
export const REMOVE_DISCOUNT = "@estimates/REMOVE_DISCOUNT";
export const RESET_DISCOUNTS = "@estimates/RESET_DISCOUNTS";
export const RESET_PAYMENT_TERM = "@estimates/RESET_PAYMENT_TERM";
export const REMOVE_ESTIMATE = "@estimates/REMOVE_ESTIMATE";

export const EstimateActions = {
  receiveEstimateOverviews: (estimates: Partial<IEstimateOverviewData>[]) =>
    createAction(RECEIVE_ESTIMATE_OVERVIEWS, { estimates }),
  fetchEstimate: (id: number) => createAction(FETCH_ESTIMATE, { id }),
  receiveEstimate: (estimate: Partial<IEstimateData>) => createAction(RECEIVE_ESTIMATE, { estimate }),
  setEstimateLoaded: (id: number) => createAction(SET_ESTIMATE_LOADED, { id }),
  discardEstimate: (id: number) => createAction(DISCARD_ESTIMATE, { id }),
  setEstimateDocsLoaded: (flag: boolean) => createAction(SET_ESTIMATE_DOCS_LOADED, flag),
  setCurrentEstimateId: (id: Nullable<number>) => createAction(SET_CURRENT_ESTIMATE_ID, { id }),
  setCurrentListingType: (listingType: EstimateListingType) => createAction(SET_CURRENT_LISTING_TYPE, { listingType }),
  receiveEstimateDocuments: (estimateDocs: IDocumentData[]) =>
    createAction(RECEIVE_ESTIMATE_DOCUMENTS, { estimateDocs }),
  resetEstimateDocuments: () => createAction(RESET_ESTIMATE_DOCUMENTS),

  updateSubtotals: (estimateId: Nullable<number>) => createAction(UPDATE_ESTIMATE_SUBTOTALS, { estimateId }),
  updateDiscountTotals: (estimateId: Nullable<number>) => createAction(UPDATE_ESTIMATE_DISCOUNT_TOTALS, { estimateId }),
  updateTaxTotals: (estimateId: Nullable<number>) => createAction(UPDATE_ESTIMATE_TAX_TOTALS, { estimateId }),
  updatePaymentTermsTotals: (estimateId: Nullable<number>) =>
    createAction(UPDATE_ESTIMATE_PAYMENT_TERMS_TOTALS, { estimateId }),
  updateEstimateFinanceOptionPayments: (estimateId: Nullable<number>) =>
    createAction(UPDATE_ESTIMATE_FINANCE_OPTION_PAYMENTS, { estimateId }),

  // Actions for managing unsaved estimates
  decrementUnsavedId: () => createAction(DECREMENT_ESTIMATE_UNSAVED_ID),
  setEstimateById: (estimateId: number, estimate: Partial<IEstimateData>) =>
    createAction(SET_ESTIMATE_BY_ID, { estimateId, estimate }),
  copyEstimateToLastSaved: (estimateId: number) => createAction(COPY_ESTIMATE_TO_LAST_SAVED, { estimateId }),
  baselinePaymentTermFinancials: (estimateId: number, financedAmount: number, downPayment: number) =>
    createAction(BASELINE_PAYMENT_TERM_FINANCIALS, { estimateId, financedAmount, downPayment }),
  updateEstimateMetadata: (estimateId: number, metadataUpdate: Record<string, unknown>) =>
    createAction(UPDATE_ESTIMATE_METADATA, { estimateId, metadataUpdate }),

  setAutoOpenLineItem: (lineItemId: Nullable<number>) => createAction(SET_AUTO_OPEN_LINE_ITEM, { lineItemId }),
  consumeAutoOpenLineItem: () => createAction(CONSUME_AUTO_OPEN_LINE_ITEM),

  // Commission Calculator (greenline/custom server-computed methods). Accepts a Partial so the error
  // path can reset the slot with `{}` (the reducer funnels it through estimateCommissionFromJSON,
  // which backfills defaults) without an unsound cast.
  fetchEstimateCommission: () => createAction(FETCH_ESTIMATE_COMMISSION),
  receiveEstimateCommission: (commissionData: Partial<IEstimateCommissionData>) =>
    createAction(RECEIVE_ESTIMATE_COMMISSION, { commissionData }),

  addDiscount: (estimateId: number, discountData: Partial<IEstimateDiscountData>) =>
    createAction(ADD_DISCOUNT, { estimateId, discountData }),
  addPaymentTerm: (estimateId: number, template: PaymentTermRecord) =>
    createAction(ADD_PAYMENT_TERM, { estimateId, template }),
  receiveCalculations: (financedAmount: number, calculations: ICalculatedFinancingData[]) =>
    createAction(RECEIVE_CALCULATIONS, { financedAmount, calculations }),
  removeDiscount: (estimateId: IdType, discount: EstimateDiscountRecord | string) =>
    createAction(REMOVE_DISCOUNT, { estimateId, discount }),
  resetDiscounts: (estimateId: number) => createAction(RESET_DISCOUNTS, { estimateId }),
  resetPaymentTerms: (estimateId: Nullable<number>) => createAction(RESET_PAYMENT_TERM, { estimateId }),
  removeEstimate: (estimateId: Nullable<number>) => createAction(REMOVE_ESTIMATE, { estimateId }),
};

export type EstimateActions = ActionsUnion<typeof EstimateActions>;
